/**
 * Story 061 — Turbine-cell drill-down affordance (AC15).
 *
 * Renders the parent turbine's composed `concatenatedName` as an
 * underlined `<button>` so Tab reaches it and Enter/Space activate the
 * drill-down. Click opens the Story 057 Turbine detail modal in-place
 * over the Components list — the page mounts `<EditTurbineDialog>` at
 * its root and keys it on the `?turbine=<uuid>` URL param.
 *
 * Visual mirrors `apps/web/src/features/turbines/list/ParkCell.tsx`.
 */
import { Box } from "@mui/material";
import type { MouseEvent, ReactElement } from "react";

export interface TurbineLinkCellProps {
  concatenatedName: string;
  onOpen: () => void;
}

export function TurbineLinkCell({
  concatenatedName,
  onOpen,
}: TurbineLinkCellProps): ReactElement {
  function handleClick(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    onOpen();
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      // Underline colours from the JS palette — `var(--mui-palette-*)` emits
      // nothing in this non-CSS-vars theme and fell back to `currentColor`,
      // making the underline invisible. See `ParkCell` (SB6-G2 Bug-7).
      sx={(theme) => ({
        background: "none",
        border: "none",
        padding: 0,
        color: "text.primary",
        cursor: "pointer",
        textAlign: "inherit",
        fontFamily: "inherit",
        fontSize: "inherit",
        textDecoration: "underline",
        textUnderlineOffset: "3px",
        textDecorationColor: theme.palette.text.secondary,
        "&:hover, &:focus-visible": {
          textDecorationColor: theme.palette.primary.main,
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
          borderRadius: "2px",
        },
      })}
    >
      {concatenatedName}
    </Box>
  );
}
